import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CheckIcon from '@mui/icons-material/Check';
import { ProjekttervezesFeladat, ProjekttervezesSor } from '../services/projekttervezesService';
import {
  addDays,
  daysBetweenInclusive,
  endOfMonth,
  parseApiDate,
  startOfDay,
  startOfMonth,
} from '../utils/workDays';

export const GANTT_DAY_WIDTH = 28;
export const GANTT_WEEK_WIDTH = 48;
export const GANTT_MONTH_WIDTH = 72;
export const GANTT_ROW_HEIGHT = 44;
export const GANTT_HEADER_HEIGHT = 52;
export const ROW_DIVIDER_COLOR = '#c5ccd6';
export const COMPLETED_ROW_BG = '#eef8ee';
export const COMPLETED_ROW_STRIPE_BG = '#dce8dc';
export const CHILD_ROW_BG = '#f3f4f6';
const HEADER_ROW_HEIGHT = 26;

export type GanttZoomLevel = 'day' | 'week' | 'month';

interface TimelineSegment {
  label: string;
  startDate: Date;
  dayCount: number;
}

interface VisibleRange {
  start: Date;
  end: Date;
  segments: TimelineSegment[];
  subSegments: TimelineSegment[];
  totalDays: number;
}

export type { VisibleRange };

export interface GanttTimelineState {
  zoomLevel: GanttZoomLevel;
  viewStart: Date;
  viewportWidth: number;
  setViewportWidth: (width: number) => void;
  setZoomLevel: (level: GanttZoomLevel) => void;
  navigatePrev: () => void;
  navigateNext: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  visibleRange: VisibleRange;
  headerHeight: number;
}

const ZOOM_ORDER: GanttZoomLevel[] = ['day', 'week', 'month'];

const getStepDays = (zoomLevel: GanttZoomLevel): number => {
  switch (zoomLevel) {
    case 'day':
      return 14;
    case 'week':
      return 28;
    case 'month':
      return 30;
    default:
      return 14;
  }
};

const alignViewStart = (date: Date, zoomLevel: GanttZoomLevel): Date => {
  if (zoomLevel === 'month') {
    return startOfMonth(date);
  }
  if (zoomLevel === 'week') {
    const day = date.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    return addDays(date, diff);
  }
  return startOfDay(date);
};

const buildMonthSegments = (start: Date, end: Date): TimelineSegment[] => {
  const segments: TimelineSegment[] = [];
  let cursor = startOfMonth(start);
  const rangeEnd = end;

  while (cursor <= rangeEnd) {
    const monthEnd = endOfMonth(cursor);
    const segStart = cursor < start ? start : cursor;
    const segEnd = monthEnd > rangeEnd ? rangeEnd : monthEnd;
    const dayCount = daysBetweenInclusive(segStart, segEnd);

    if (dayCount > 0) {
      segments.push({
        label: cursor.toLocaleDateString('hu-HU', { year: 'numeric', month: 'long' }).toUpperCase(),
        startDate: segStart,
        dayCount,
      });
    }

    cursor = addDays(endOfMonth(cursor), 1);
  }

  return segments;
};

const buildDaySubSegments = (start: Date, end: Date): TimelineSegment[] => {
  const segments: TimelineSegment[] = [];
  let cursor = start;

  while (cursor <= end) {
    segments.push({
      label: String(cursor.getDate()),
      startDate: cursor,
      dayCount: 1,
    });
    cursor = addDays(cursor, 1);
  }

  return segments;
};

const buildWeekSubSegments = (start: Date, end: Date): TimelineSegment[] => {
  const segments: TimelineSegment[] = [];
  let cursor = alignViewStart(start, 'week');

  while (cursor <= end) {
    const weekEnd = addDays(cursor, 6);
    const segEnd = weekEnd > end ? end : weekEnd;
    const dayCount = daysBetweenInclusive(cursor, segEnd);

    segments.push({
      label: `${cursor.getDate()}.–${segEnd.getDate()}.`,
      startDate: cursor,
      dayCount,
    });

    cursor = addDays(cursor, 7);
  }

  return segments;
};

const buildYearSegments = (start: Date, end: Date): TimelineSegment[] => {
  const segments: TimelineSegment[] = [];
  let year = start.getFullYear();
  const endYear = end.getFullYear();

  while (year <= endYear) {
    const yearStart = startOfDay(new Date(year, 0, 1));
    const yearEnd = startOfDay(new Date(year, 11, 31));
    const segStart = yearStart < start ? start : yearStart;
    const segEnd = yearEnd > end ? end : yearEnd;
    const dayCount = daysBetweenInclusive(segStart, segEnd);

    if (dayCount > 0) {
      segments.push({
        label: String(year),
        startDate: segStart,
        dayCount,
      });
    }

    year += 1;
  }

  return segments;
};

const buildMonthSubSegments = (start: Date, end: Date): TimelineSegment[] => {
  return buildMonthSegments(start, end);
};

const computeVisibleRange = (
  viewStart: Date,
  viewportWidth: number,
  zoomLevel: GanttZoomLevel,
): VisibleRange => {
  if (viewportWidth <= 0) {
    const fallbackEnd = addDays(viewStart, 30);
    return {
      start: viewStart,
      end: fallbackEnd,
      segments: buildMonthSegments(viewStart, fallbackEnd),
      subSegments: buildDaySubSegments(viewStart, fallbackEnd),
      totalDays: daysBetweenInclusive(viewStart, fallbackEnd),
    };
  }

  let end: Date;

  if (zoomLevel === 'day') {
    const visibleDays = Math.max(1, Math.floor(viewportWidth / GANTT_DAY_WIDTH));
    end = addDays(viewStart, visibleDays - 1);
  } else if (zoomLevel === 'week') {
    const visibleWeeks = Math.max(1, Math.floor(viewportWidth / GANTT_WEEK_WIDTH));
    end = addDays(viewStart, visibleWeeks * 7 - 1);
  } else {
    const visibleMonths = Math.max(1, Math.floor(viewportWidth / GANTT_MONTH_WIDTH));
    let cursor = startOfMonth(viewStart);
    for (let i = 0; i < visibleMonths - 1; i += 1) {
      cursor = addDays(endOfMonth(cursor), 1);
    }
    end = endOfMonth(cursor);
  }

  const segments = zoomLevel === 'month'
    ? buildYearSegments(viewStart, end)
    : buildMonthSegments(viewStart, end);
  let subSegments: TimelineSegment[];

  switch (zoomLevel) {
    case 'week':
      subSegments = buildWeekSubSegments(viewStart, end);
      break;
    case 'month':
      subSegments = buildMonthSubSegments(viewStart, end);
      break;
    default:
      subSegments = buildDaySubSegments(viewStart, end);
  }

  return {
    start: viewStart,
    end,
    segments,
    subSegments,
    totalDays: daysBetweenInclusive(viewStart, end),
  };
};

export const useGanttTimeline = (rowCount: number): GanttTimelineState => {
  const today = useMemo(() => startOfDay(new Date()), []);
  const [zoomLevel, setZoomLevelState] = useState<GanttZoomLevel>('day');
  const [viewStart, setViewStart] = useState(() => alignViewStart(startOfMonth(today), 'day'));
  const [viewportWidth, setViewportWidth] = useState(0);

  useEffect(() => {
    setViewStart(alignViewStart(startOfMonth(today), zoomLevel));
  }, [rowCount, today]);

  const visibleRange = useMemo(
    () => computeVisibleRange(viewStart, viewportWidth, zoomLevel),
    [viewStart, viewportWidth, zoomLevel],
  );

  const setZoomLevel = useCallback((level: GanttZoomLevel) => {
    setZoomLevelState(level);
    setViewStart((current) => alignViewStart(current, level));
  }, []);

  const navigatePrev = useCallback(() => {
    setViewStart((current) => {
      const aligned = alignViewStart(current, zoomLevel);
      return addDays(aligned, -getStepDays(zoomLevel));
    });
  }, [zoomLevel]);

  const navigateNext = useCallback(() => {
    setViewStart((current) => {
      const aligned = alignViewStart(current, zoomLevel);
      return addDays(aligned, getStepDays(zoomLevel));
    });
  }, [zoomLevel]);

  const zoomIn = useCallback(() => {
    const index = ZOOM_ORDER.indexOf(zoomLevel);
    if (index > 0) {
      setZoomLevel(ZOOM_ORDER[index - 1]);
    }
  }, [zoomLevel, setZoomLevel]);

  const zoomOut = useCallback(() => {
    const index = ZOOM_ORDER.indexOf(zoomLevel);
    if (index < ZOOM_ORDER.length - 1) {
      setZoomLevel(ZOOM_ORDER[index + 1]);
    }
  }, [zoomLevel, setZoomLevel]);

  const headerHeight = GANTT_HEADER_HEIGHT;

  return {
    zoomLevel,
    viewStart,
    viewportWidth,
    setViewportWidth,
    setZoomLevel,
    navigatePrev,
    navigateNext,
    zoomIn,
    zoomOut,
    visibleRange,
    headerHeight,
  };
};

const segmentWidth = (
  segment: TimelineSegment,
  zoomLevel: GanttZoomLevel,
  viewportWidth: number,
  totalDays: number,
): number => {
  if (zoomLevel === 'day') {
    return segment.dayCount * GANTT_DAY_WIDTH;
  }
  return (segment.dayCount / totalDays) * viewportWidth;
};

const getDayWidth = (
  zoomLevel: GanttZoomLevel,
  viewportWidth: number,
  totalDays: number,
): number => {
  if (zoomLevel === 'day') {
    return GANTT_DAY_WIDTH;
  }
  return viewportWidth / Math.max(totalDays, 1);
};

/** Oszlop közepe – a napi ráccsal egyező koordináta-rendszer */
export const dateToColumnCenter = (
  date: Date | null,
  range: VisibleRange,
  zoomLevel: GanttZoomLevel,
  viewportWidth: number,
): number | null => {
  if (!date) {
    return null;
  }

  const { start, end, totalDays } = range;
  const normalizedDate = startOfDay(date);

  if (normalizedDate < startOfDay(start)) {
    return 0;
  }
  if (normalizedDate > startOfDay(end)) {
    const dayWidth = getDayWidth(zoomLevel, viewportWidth, totalDays);
    return totalDays * dayWidth;
  }

  const dayIndex = daysBetweenInclusive(start, normalizedDate) - 1;
  const dayWidth = getDayWidth(zoomLevel, viewportWidth, totalDays);
  return dayIndex * dayWidth + dayWidth / 2;
};

export const dateToBarGeometry = (
  startDate: Date | null,
  endDate: Date | null,
  range: VisibleRange,
  zoomLevel: GanttZoomLevel,
  viewportWidth: number,
): { left: number; width: number } | null => {
  const startCenter = dateToColumnCenter(startDate, range, zoomLevel, viewportWidth);
  const endCenter = dateToColumnCenter(endDate, range, zoomLevel, viewportWidth);

  if (startCenter == null || endCenter == null) {
    return null;
  }

  const minWidth = 6;
  if (Math.abs(endCenter - startCenter) < 0.5) {
    return { left: startCenter - minWidth / 2, width: minWidth };
  }

  const left = Math.min(startCenter, endCenter);
  const right = Math.max(startCenter, endCenter);
  return { left, width: Math.max(right - left, minWidth) };
};

export const columnCenterToDate = (
  x: number,
  range: VisibleRange,
  zoomLevel: GanttZoomLevel,
  viewportWidth: number,
): Date => {
  const { start, totalDays } = range;
  const dayWidth = getDayWidth(zoomLevel, viewportWidth, totalDays);
  if (dayWidth <= 0) {
    return start;
  }

  const dayIndex = Math.round((x - dayWidth / 2) / dayWidth);
  const clampedIndex = Math.max(0, Math.min(totalDays - 1, dayIndex));
  return addDays(start, clampedIndex);
};

export const positionToDate = (
  x: number,
  range: VisibleRange,
  zoomLevel: GanttZoomLevel,
  viewportWidth: number,
): Date => columnCenterToDate(x, range, zoomLevel, viewportWidth);

interface GanttViewportMeasureProps {
  onWidthChange: (width: number) => void;
  children: React.ReactNode;
}

export const GanttViewportMeasure: React.FC<GanttViewportMeasureProps> = ({ onWidthChange, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const onWidthChangeRef = useRef(onWidthChange);
  onWidthChangeRef.current = onWidthChange;

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return undefined;
    }

    const update = () => onWidthChangeRef.current(el.getBoundingClientRect().width);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Box ref={ref} sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      {children}
    </Box>
  );
};

interface GanttTimelineControlsProps {
  timeline: GanttTimelineState;
}

export const GanttTimelineControls: React.FC<GanttTimelineControlsProps> = ({ timeline }) => {
  const { zoomLevel, navigatePrev, navigateNext, zoomIn, zoomOut } = timeline;
  const zoomLabel = zoomLevel === 'day' ? 'Napi' : zoomLevel === 'week' ? 'Heti' : 'Havi';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        backgroundColor: '#f8f9fb',
        borderRadius: 1,
        border: '1px solid #e5e9ef',
        px: 0.25,
      }}
    >
      <Tooltip title="Előző időszak">
        <IconButton size="small" onClick={navigatePrev}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Következő időszak">
        <IconButton size="small" onClick={navigateNext}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Box sx={{ width: 1, height: 20, backgroundColor: '#e5e9ef', mx: 0.25 }} />
      <Tooltip title="Nagyítás (részletesebb nézet)">
        <span>
          <IconButton size="small" onClick={zoomIn} disabled={zoomLevel === 'day'}>
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Kicsinyítés (áttekintő nézet)">
        <span>
          <IconButton size="small" onClick={zoomOut} disabled={zoomLevel === 'month'}>
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Typography variant="caption" sx={{ px: 0.75, color: '#5f6b7a', fontWeight: 600, fontSize: 10 }}>
        {zoomLabel}
      </Typography>
    </Box>
  );
};

interface GanttTimelineHeaderProps {
  timeline: GanttTimelineState;
  viewportWidth: number;
}

export const GanttTimelineHeader: React.FC<GanttTimelineHeaderProps> = ({ timeline, viewportWidth }) => {
  const { zoomLevel, visibleRange, headerHeight } = timeline;
  const { segments, subSegments, totalDays } = visibleRange;

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        height: headerHeight,
        backgroundColor: '#f8f9fb',
        borderBottom: `1px solid ${ROW_DIVIDER_COLOR}`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', height: HEADER_ROW_HEIGHT, borderBottom: `1px solid ${ROW_DIVIDER_COLOR}`, overflow: 'hidden' }}>
        {segments.map((segment) => (
          <Box
            key={`${segment.label}-${segment.startDate.toISOString()}`}
            sx={{
              width: segmentWidth(segment, zoomLevel, viewportWidth, totalDays),
              flexShrink: 0,
              borderRight: `1px solid ${ROW_DIVIDER_COLOR}`,
              px: 1,
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
            }}
          >
            <Typography variant="caption" noWrap sx={{ fontWeight: 700, color: '#5f6b7a' }}>
              {segment.label}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', height: HEADER_ROW_HEIGHT, overflow: 'hidden' }}>
        {subSegments.map((segment, index) => (
          <Box
            key={`${segment.startDate.toISOString()}-${index}`}
            sx={{
              width: segmentWidth(segment, zoomLevel, viewportWidth, totalDays),
              flexShrink: 0,
              borderRight: `1px solid ${ROW_DIVIDER_COLOR}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: index % 2 === 1 ? '#fafbfc' : '#fff',
            }}
          >
            <Typography variant="caption" sx={{ color: '#7a8699', fontSize: 10 }}>
              {segment.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const GanttTodayMarker: React.FC<{
  today: Date;
  visibleRange: VisibleRange;
  zoomLevel: GanttZoomLevel;
  viewportWidth: number;
}> = ({ today, visibleRange, zoomLevel, viewportWidth }) => {
  const todayCenter = dateToColumnCenter(today, visibleRange, zoomLevel, viewportWidth);
  if (todayCenter == null || viewportWidth <= 0) {
    return null;
  }

  const timelineWidth = visibleRange.totalDays * getDayWidth(zoomLevel, viewportWidth, visibleRange.totalDays);
  if (todayCenter < 0 || todayCenter > timelineWidth) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: todayCenter,
        width: 2,
        marginLeft: '-1px',
        backgroundColor: '#e53935',
        zIndex: 20,
        pointerEvents: 'none',
      }}
    />
  );
};

interface GanttRowBarsProps {
  row: ProjekttervezesSor;
  visibleRange: VisibleRange;
  zoomLevel: GanttZoomLevel;
  viewportWidth: number;
}

const GanttRowBarsComponent: React.FC<GanttRowBarsProps> = ({
  row,
  visibleRange,
  zoomLevel,
  viewportWidth,
}) => {
  const today = useMemo(() => startOfDay(new Date()), []);
  const { subSegments, totalDays } = visibleRange;

  const plannedStart = parseApiDate(row.projektKezdete);
  const plannedEnd = parseApiDate(row.tervezettBefejezes);
  const actualStart = parseApiDate(row.tenylegesProjektKezdete);
  const actualEnd = parseApiDate(row.tenylegesBefejezes);
  const isCompleted = !!actualEnd;
  const actualElapsedEnd = actualEnd ?? today;
  const isOverdue = !actualEnd && plannedEnd && today > plannedEnd;

  const plannedGeometry = dateToBarGeometry(plannedStart, plannedEnd, visibleRange, zoomLevel, viewportWidth);
  const actualGeometry = dateToBarGeometry(actualStart, actualElapsedEnd, visibleRange, zoomLevel, viewportWidth);
  const overdueGeometry = isOverdue && plannedEnd
    ? dateToBarGeometry(plannedEnd, today, visibleRange, zoomLevel, viewportWidth)
    : null;
  const plannedEndCenter = dateToColumnCenter(plannedEnd, visibleRange, zoomLevel, viewportWidth);

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        width: '100%',
        position: 'relative',
        height: '100%',
        backgroundColor: isCompleted ? COMPLETED_ROW_BG : '#fff',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        {subSegments.map((segment, index) => (
          <Box
            key={`${segment.startDate.toISOString()}-${index}`}
            sx={{
              width: segmentWidth(segment, zoomLevel, viewportWidth, totalDays),
              flexShrink: 0,
              borderRight: `1px solid ${ROW_DIVIDER_COLOR}`,
              backgroundColor: isCompleted
                ? (index % 2 === 1 ? COMPLETED_ROW_STRIPE_BG : COMPLETED_ROW_BG)
                : (index % 2 === 1 ? 'rgba(250, 251, 252, 0.8)' : 'transparent'),
            }}
          />
        ))}
      </Box>

      {plannedGeometry && (
        <Box
          sx={{
            position: 'absolute',
            top: 14,
            left: plannedGeometry.left,
            width: plannedGeometry.width,
            height: 16,
            backgroundColor: '#d9dde3',
            borderRadius: 1,
            zIndex: 1,
          }}
        />
      )}

      {actualGeometry && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: actualGeometry.left,
            width: actualGeometry.width,
            height: 12,
            backgroundColor: '#9aa3ad',
            borderRadius: 1,
            zIndex: 1,
          }}
        />
      )}

      {overdueGeometry && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: overdueGeometry.left,
            width: overdueGeometry.width,
            height: 12,
            backgroundColor: '#f2d58c',
            borderRadius: 1,
            zIndex: 1,
          }}
        />
      )}

      {plannedEndCenter != null && (
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            left: plannedEndCenter - 4,
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: '#e53935',
            zIndex: 3,
          }}
        />
      )}

      <GanttTodayMarker
        today={today}
        visibleRange={visibleRange}
        zoomLevel={zoomLevel}
        viewportWidth={viewportWidth}
      />
    </Box>
  );
};

export const GanttRowBars = React.memo(GanttRowBarsComponent);

type FeladatDragMode = 'move' | 'resize-start' | 'resize-end';

interface GanttFeladatBarProps {
  feladat: ProjekttervezesFeladat;
  visibleRange: VisibleRange;
  zoomLevel: GanttZoomLevel;
  viewportWidth: number;
  onDatesChange: (
    feladatId: number,
    kezdetiDatum: Date | null,
    befejezesDatum: Date | null,
  ) => void;
  readOnly?: boolean;
}

const GanttFeladatBarComponent: React.FC<GanttFeladatBarProps> = ({
  feladat,
  visibleRange,
  zoomLevel,
  viewportWidth,
  onDatesChange,
  readOnly = false,
}) => {
  const { subSegments, totalDays } = visibleRange;
  const dragStateRef = useRef<{
    mode: FeladatDragMode;
    startX: number;
    initialStart: Date | null;
    initialEnd: Date | null;
  } | null>(null);

  const [draftDates, setDraftDates] = useState<{
    start: Date | null;
    end: Date | null;
  } | null>(null);

  const startDate = draftDates?.start ?? parseApiDate(feladat.kezdetiDatum);
  const endDate = draftDates?.end ?? parseApiDate(feladat.befejezesDatum);
  const isCanceled = feladat.status === 'canceled';
  const isFinished = feladat.status === 'finished';
  const today = useMemo(() => startOfDay(new Date()), []);

  const barGeometry = dateToBarGeometry(startDate, endDate, visibleRange, zoomLevel, viewportWidth);

  useEffect(() => {
    setDraftDates(null);
  }, [feladat.id, feladat.kezdetiDatum, feladat.befejezesDatum]);

  const finishDrag = useCallback(() => {
    const dragState = dragStateRef.current;
    dragStateRef.current = null;

    if (!dragState) {
      return;
    }

    setDraftDates((currentDraft) => {
      if (currentDraft) {
        onDatesChange(feladat.id, currentDraft.start, currentDraft.end);
      }
      return null;
    });
  }, [feladat.id, onDatesChange]);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState || viewportWidth <= 0 || !dragState.initialStart || !dragState.initialEnd) {
        return;
      }

      const deltaX = event.clientX - dragState.startX;
      const initialStartCenter = dateToColumnCenter(
        dragState.initialStart,
        visibleRange,
        zoomLevel,
        viewportWidth,
      ) ?? 0;
      const initialEndCenter = dateToColumnCenter(
        dragState.initialEnd,
        visibleRange,
        zoomLevel,
        viewportWidth,
      ) ?? 0;
      const durationDays = Math.max(
        0,
        daysBetweenInclusive(dragState.initialStart, dragState.initialEnd) - 1,
      );

      let nextStart = dragState.initialStart;
      let nextEnd = dragState.initialEnd;

      if (dragState.mode === 'move') {
        const nextStartCenter = initialStartCenter + deltaX;
        nextStart = columnCenterToDate(nextStartCenter, visibleRange, zoomLevel, viewportWidth);
        nextEnd = addDays(nextStart, durationDays);
      } else if (dragState.mode === 'resize-start') {
        const nextStartCenter = initialStartCenter + deltaX;
        nextStart = columnCenterToDate(nextStartCenter, visibleRange, zoomLevel, viewportWidth);
        if (nextStart > nextEnd) {
          nextStart = nextEnd;
        }
      } else if (dragState.mode === 'resize-end') {
        const nextEndCenter = initialEndCenter + deltaX;
        nextEnd = columnCenterToDate(nextEndCenter, visibleRange, zoomLevel, viewportWidth);
        if (nextEnd < nextStart) {
          nextEnd = nextStart;
        }
      }

      setDraftDates({ start: nextStart, end: nextEnd });
    };

    const handleMouseUp = () => {
      finishDrag();
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [finishDrag, viewportWidth, visibleRange, zoomLevel]);

  const handleMouseDown = (event: React.MouseEvent, mode: FeladatDragMode) => {
    if (readOnly || !startDate || !endDate) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    dragStateRef.current = {
      mode,
      startX: event.clientX,
      initialStart: startDate,
      initialEnd: endDate,
    };
    setDraftDates({ start: startDate, end: endDate });
  };

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        width: '100%',
        position: 'relative',
        height: '100%',
        backgroundColor: CHILD_ROW_BG,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        {subSegments.map((segment, index) => (
          <Box
            key={`${segment.startDate.toISOString()}-${index}`}
            sx={{
              width: segmentWidth(segment, zoomLevel, viewportWidth, totalDays),
              flexShrink: 0,
              borderRight: `1px solid ${ROW_DIVIDER_COLOR}`,
              backgroundColor: index % 2 === 1 ? '#eceff3' : 'transparent',
            }}
          />
        ))}
      </Box>

      {barGeometry && startDate && endDate && (
        <Box
          sx={{
            position: 'absolute',
            top: 14,
            left: barGeometry.left,
            width: barGeometry.width,
            height: 16,
            backgroundColor: feladat.szin,
            borderRadius: 1,
            zIndex: 3,
            opacity: isCanceled ? 0.45 : 1,
            border: isCanceled ? '1px dashed #8a96a3' : 'none',
            cursor: readOnly ? 'default' : 'grab',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            userSelect: 'none',
            pr: 0.25,
            '&:active': { cursor: readOnly ? 'default' : 'grabbing' },
          }}
          onMouseDown={(event) => {
            if (readOnly) {
              return;
            }
            const rect = (event.currentTarget as HTMLDivElement).getBoundingClientRect();
            const offsetX = event.clientX - rect.left;
            const handleSize = 6;
            if (offsetX <= handleSize) {
              handleMouseDown(event, 'resize-start');
            } else if (offsetX >= rect.width - handleSize) {
              handleMouseDown(event, 'resize-end');
            } else {
              handleMouseDown(event, 'move');
            }
          }}
        >
          <Box sx={{ position: 'absolute', left: 0, width: 6, height: '100%', cursor: 'ew-resize' }} />
          <Box sx={{ position: 'absolute', right: 0, width: 6, height: '100%', cursor: 'ew-resize' }} />
          {isFinished && (
            <CheckIcon
              sx={{
                fontSize: 14,
                color: '#fff',
                filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.45))',
                pointerEvents: 'none',
              }}
            />
          )}
        </Box>
      )}

      <GanttTodayMarker
        today={today}
        visibleRange={visibleRange}
        zoomLevel={zoomLevel}
        viewportWidth={viewportWidth}
      />
    </Box>
  );
};

export const GanttFeladatBar = React.memo(GanttFeladatBarComponent);

interface GanttCsoportBarProps {
  szin: string;
  startDate: Date | null;
  endDate: Date | null;
  visibleRange: VisibleRange;
  zoomLevel: GanttZoomLevel;
  viewportWidth: number;
}

const GanttCsoportBarComponent: React.FC<GanttCsoportBarProps> = ({
  szin,
  startDate,
  endDate,
  visibleRange,
  zoomLevel,
  viewportWidth,
}) => {
  const { subSegments, totalDays } = visibleRange;
  const today = useMemo(() => startOfDay(new Date()), []);
  const barGeometry = dateToBarGeometry(startDate, endDate, visibleRange, zoomLevel, viewportWidth);

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        width: '100%',
        position: 'relative',
        height: '100%',
        backgroundColor: CHILD_ROW_BG,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        {subSegments.map((segment, index) => (
          <Box
            key={`${segment.startDate.toISOString()}-${index}`}
            sx={{
              width: segmentWidth(segment, zoomLevel, viewportWidth, totalDays),
              flexShrink: 0,
              borderRight: `1px solid ${ROW_DIVIDER_COLOR}`,
              backgroundColor: index % 2 === 1 ? '#eceff3' : 'transparent',
            }}
          />
        ))}
      </Box>

      {barGeometry && startDate && endDate && (
        <Box
          sx={{
            position: 'absolute',
            top: 14,
            left: barGeometry.left,
            width: barGeometry.width,
            height: 16,
            backgroundColor: szin,
            borderRadius: 1,
            zIndex: 3,
            opacity: 0.88,
            border: '1px solid rgba(0, 0, 0, 0.12)',
          }}
        />
      )}

      <GanttTodayMarker
        today={today}
        visibleRange={visibleRange}
        zoomLevel={zoomLevel}
        viewportWidth={viewportWidth}
      />
    </Box>
  );
};

export const GanttCsoportBar = React.memo(GanttCsoportBarComponent);
